import React from 'react';

import { parseNullableValue } from '@clearverify/utils';

import { PeosListDocument, useSendInvitesToPeoByPeoPpMutation } from 'src/api';
import { PEO_UPLOAD_BY_PEO_PP_TEMPLATE } from 'src/assets/peo';
import { BulkUploadForm, BulkUploadTable } from 'src/components/bulkUpload';
import type { TableColumn } from 'src/components/table';
import { PEO_UPLOAD_BY_PEO_PP_TEMPLATE_NAME } from 'src/constants/templates';
import { useNotification } from 'src/hooks/app';
import { usePeosUnderPeoPpListQueryVariables } from 'src/hooks/peo';
import { TableDataProvider } from 'src/providers/TableDataProvider';
import { clearEntitiesData } from 'src/utils/bulkUpload';

import { StepId, templateSchema } from './constants';
import { ExtendedInvitedPeoData, InvitedPeoData } from './types';
import { convertDataWithSchema } from './utils/convertDataWithSchema';
import { validatePeos } from './utils/validatePeos';

interface InvitePeoByPeoPpModalContentProps {
  closeModal: () => void;
  switchClosability: (isClosureAllowed: boolean) => void;
  peoPayrollProviderId?: string;
}

export const InvitePeoByPeoPpModalContent: React.FC<InvitePeoByPeoPpModalContentProps> = ({
  closeModal,
  switchClosability,
  peoPayrollProviderId,
}) => {
  const [currentStep, setCurrentStep] = React.useState<StepId>(StepId.Upload);
  const [peos, setPeos] = React.useState<ExtendedInvitedPeoData[]>([]);

  const { queryVariables } = usePeosUnderPeoPpListQueryVariables(peoPayrollProviderId);

  const { createApolloContext } = useNotification();

  const [sendInvites, { loading: sendInvitesLoading }] = useSendInvitesToPeoByPeoPpMutation({
    context: createApolloContext({
      errorMessage: 'Failed to invite PEOs',
      successMessage: 'PEOs have been successfully invited',
    }),
    refetchQueries: [{ query: PeosListDocument, variables: queryVariables }],
  });

  const handlePeosData = React.useCallback((peos: InvitedPeoData[]) => {
    const validatedPeos = validatePeos(peos);
    setPeos(validatedPeos);
    setCurrentStep(StepId.Review);
  }, []);

  const handleSaveChanges = React.useCallback(async () => {
    try {
      switchClosability(false);

      const validPeos = peos.filter(peo => peo.valid);

      if (validPeos.length > 0) {
        await sendInvites({
          variables: {
            input: { peos: clearEntitiesData(validPeos), peoPayrollProviderId },
          },
        });
      }

      switchClosability(true);
      closeModal();
    } catch {
      switchClosability(true);
    }
  }, [closeModal, peoPayrollProviderId, peos, sendInvites, switchClosability]);

  const handleCancel = React.useCallback(() => {
    closeModal();
  }, [closeModal]);

  const handleBack = React.useCallback(() => {
    setPeos([]);
    setCurrentStep(StepId.Upload);
  }, []);

  const loading = sendInvitesLoading;

  if (currentStep === StepId.Upload) {
    return (
      <BulkUploadForm
        convertDataWithSchema={convertDataWithSchema}
        templateSchema={templateSchema}
        templateDownloadLink={PEO_UPLOAD_BY_PEO_PP_TEMPLATE}
        templateDownloadName={PEO_UPLOAD_BY_PEO_PP_TEMPLATE_NAME}
        topNotice={uploadFormTopNotice}
        handleEntitiesData={handlePeosData}
        handleCancel={handleCancel}
        nextStepLoading={loading}
        switchClosability={switchClosability}
      />
    );
  }

  return (
    <TableDataProvider>
      <BulkUploadTable
        entitites={peos}
        columns={tableColumns}
        invalidEntititesMessage={invalidEntititesMessage}
        handleSaveChanges={handleSaveChanges}
        handleBack={handleBack}
        loading={loading}
      />
    </TableDataProvider>
  );
};

const uploadFormTopNotice = `Using this modal, you can upload the file with several PEOs. Be aware that using this
form you only upload head PEO company information. In order to upload Employers under a
specific PEO you need to use the Employer upload.`;

const invalidEntititesMessage =
  'There were some errors while processing the file provided. Some fields are either missing or have been inaccurately formatted. If you proceed with this upload, the PEOs highlighted red will not be added.';

const tableColumns: TableColumn<ExtendedInvitedPeoData>[] = [
  {
    key: 'peoFein',
    title: 'PEO FEIN',
    render: (_valueByDataPath, peo) => {
      return peo.fein;
    },
  },
  {
    key: 'displayName',
    title: 'Display Name',
    render: (_valueByDataPath, peo) => {
      return peo.displayName;
    },
  },
  {
    key: 'industry',
    title: 'Industry',
    render: (_valueByDataPath, peo) => {
      return parseNullableValue(peo.industry);
    },
  },
  {
    key: 'corporateStructure',
    title: 'Corporate Structure',
    render: (_valueByDataPath, peo) => {
      return parseNullableValue(peo.corporateStructure);
    },
  },
  {
    key: 'taxYearEndDate',
    title: 'Tax Year End Date',
    render: (_valueByDataPath, peo) => {
      return parseNullableValue(peo.taxYearEndDate);
    },
  },
  {
    key: 'numberOfEmployees',
    title: 'Number of Employees',
    render: (_valueByDataPath, peo) => {
      return parseNullableValue(peo.numberOfEmployees);
    },
  },
  {
    key: 'hqAddress',
    title: 'HQ Address',
    render: (_valueByDataPath, peo) => {
      return peo.hqAddress;
    },
  },
  {
    key: 'hqCity',
    title: 'HQ City',
    render: (_valueByDataPath, peo) => {
      return peo.hqCity;
    },
  },
  {
    key: 'hqCounty',
    title: 'HQ County',
    render: (_valueByDataPath, peo) => {
      return parseNullableValue(peo.hqCounty);
    },
  },
  {
    key: 'hqState',
    title: 'HQ State',
    render: (_valueByDataPath, peo) => {
      return peo.hqState;
    },
  },
  {
    key: 'hqZipCode',
    title: 'HQ ZIP Code',
    render: (_valueByDataPath, peo) => {
      return peo.hqZipCode;
    },
  },
  {
    key: 'contactEmail',
    title: 'Contact Email',
    render: (_valueByDataPath, peo) => {
      return peo.contactEmail;
    },
  },
  {
    key: 'emailForInvitation',
    title: 'Email for Invitation',
    render: (_valueByDataPath, peo) => {
      return parseNullableValue(peo.emailForInvitation);
    },
  },
  {
    key: 'contactPhone',
    title: 'Contact Phone',
    render: (_valueByDataPath, peo) => {
      return parseNullableValue(peo.contactPhone);
    },
  },
];
